import "./site/error-beacon";
import { Echo, EchoRoundEvent } from "./games/echo/echo";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import { getActiveChallengeCode, acceptChallenge, createChallenge, fetchChallenge } from "./site/challenge";
import "./ui/styles.css";

const PB_KEY = "aether.echo_pb";
const HANDLE_KEY = "aether.handle";

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startEchoRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/echo", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishEchoRun(
  runId: string, handle: string, events: EchoRoundEvent[],
  wrongTaps: { round: number; i: number; seg: number; ms: number }[],
) {
  try {
    const res = await fetch(`/api/runs/echo/${runId}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, events, wrong_taps: wrongTaps }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudRound = document.getElementById("hud-round")!;
const hudPhase = document.getElementById("hud-phase")!;
const hudBest = document.getElementById("hud-best")!;
const heartsEl = document.getElementById("hud-hearts") as HTMLElement | null;
function renderHearts(livesRemaining: number) {
  if (!heartsEl) return;
  heartsEl.textContent = "❤".repeat(livesRemaining) + "♡".repeat(Math.max(0, 3 - livesRemaining));
  heartsEl.classList.toggle("danger", livesRemaining <= 1);
}
renderHearts(3);
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const audioOnlyCheckbox = document.getElementById("audio-only-checkbox") as HTMLInputElement | null;
const announcer = document.getElementById("echo-announcer") as HTMLElement | null;
// Screen-reader announcer. Echo's pillars live inside a canvas opaque to
// assistive tech, so phase/round/life transitions need a DOM polite
// region. Pure announcement — no visual change.
function announce(msg: string) {
  if (!announcer) return;
  // Force a re-read by clearing and re-setting on next tick.
  announcer.textContent = "";
  setTimeout(() => { if (announcer) announcer.textContent = msg; }, 30);
}
function prefersReducedMotion(): boolean {
  try { return matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}

// v5 — small inline heatmap shown on game-over: where the player's wrong
// taps landed across the 4 segments. Geometry mirrors the in-game ring
// (seg 0=N, 1=E, 2=S, 3=W), so the heatmap reads as "you keep mis-tapping
// the east pillar" at a glance. Local-only, never submitted.
const SEG_LABELS = ["1", "2", "3", "4"];
const SEG_TINTS = ["#00e0ff", "#ffaa11", "#ff3df0", "#66ff88"];
// Heatmap positions in a 100x100 viewBox, matching N/E/S/W layout.
const SEG_HM_XY: [number, number][] = [
  [50, 18], // N (seg 0)
  [82, 50], // E (seg 1)
  [50, 82], // S (seg 2)
  [18, 50], // W (seg 3)
];
function renderWrongTapHeatmap(wrongTaps: { seg: number }[]): string {
  const counts = [0, 0, 0, 0];
  for (const w of wrongTaps) {
    if (w.seg >= 0 && w.seg < 4) counts[w.seg] += 1;
  }
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0) return "";
  const maxC = Math.max(...counts);
  const dots = counts
    .map((c, i) => {
      const [cx, cy] = SEG_HM_XY[i];
      const r = 5 + (c / maxC) * 10;
      const opacity = c === 0 ? 0.18 : 0.35 + (c / maxC) * 0.6;
      const label = c > 0 ? `<text x="${cx}" y="${cy + 3}" text-anchor="middle" font-size="9" fill="#0a0c14" font-family="monospace">${c}</text>` : "";
      return `<circle cx="${cx}" cy="${cy}" r="${r.toFixed(1)}" fill="${SEG_TINTS[i]}" opacity="${opacity.toFixed(2)}" /><text x="${cx}" y="${cy - r - 3}" text-anchor="middle" font-size="7" fill="currentColor" opacity="0.55" font-family="monospace">${SEG_LABELS[i]}</text>${label}`;
    })
    .join("");
  return `<div class="echo-heatmap" aria-label="Wrong-tap heatmap by segment">
    <small>Wrong taps by segment (${total} total)</small>
    <svg viewBox="0 0 100 100" width="100" height="100" aria-hidden="true">
      <circle cx="50" cy="50" r="32" fill="none" stroke="currentColor" stroke-width="0.4" opacity="0.2" />
      ${dots}
    </svg>
  </div>`;
}
let heatmapEl: HTMLDivElement | null = null;
function showWrongTapHeatmap(wrongTaps: { seg: number }[]) {
  const html = renderWrongTapHeatmap(wrongTaps);
  if (!heatmapEl) {
    heatmapEl = document.createElement("div");
    heatmapEl.id = "echo-heatmap-host";
    document.querySelector(".screen-msg")?.insertBefore(heatmapEl, submitForm);
  }
  heatmapEl.innerHTML = html;
  heatmapEl.hidden = html === "";
}
function hideWrongTapHeatmap() {
  if (heatmapEl) heatmapEl.hidden = true;
}

try {
  const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
  hudBest.textContent = pb.toLocaleString();
} catch { /* private */ }

let game: Echo | null = null;
let activeRunId: string | null = null;
let activeSeed: number | null = null;
let pendingEvents: EchoRoundEvent[] | null = null;
let pendingWrongTaps: { round: number; i: number; seg: number; ms: number }[] = [];

const challengeCode = getActiveChallengeCode();
let challengeInfo: Awaited<ReturnType<typeof fetchChallenge>> = null;
if (challengeCode) {
  fetchChallenge(challengeCode).then((v) => {
    challengeInfo = v;
    if (v && !v.expired && !v.accepter) {
      msgBody.textContent = `${v.challenger.handle} challenges you — beat ${v.challenger.score.toLocaleString()}.`;
    }
  });
}

async function startRun() {
  if (game) game.dispose();
  hideWrongTapHeatmap();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startEchoRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  activeSeed = seed;
  // Per-run flag: frozen at run-start so the player can't toggle mid-run.
  // Reduced-motion auto-engages from the OS-level preference; it shortens
  // demo flashes from 320/500 to 150/100 ms.
  const audioOnly = audioOnlyCheckbox?.checked === true;
  const reducedMotion = prefersReducedMotion();

  game = new Echo(canvas, {
    onHud: (round, phase) => {
      hudRound.textContent = String(round);
      hudPhase.textContent = phase === "demo" ? "watch" : phase === "recall" ? "repeat" : "—";
      // Screen-reader announcements at phase transitions only — never
      // every frame. The polite region rate-limits itself.
      if (phase === "demo") {
        announce(`Round ${round}, demo phase. ${audioOnly ? "Listen for the sequence." : "Watch the sequence."}`);
      } else if (phase === "recall") {
        announce(`Your turn. Tap segment 1, 2, 3, or 4 to match the sequence.`);
      }
    },
    onLifeLost: (livesRemaining) => {
      renderHearts(livesRemaining);
      announce(`Wrong tap. ${livesRemaining} ${livesRemaining === 1 ? "life" : "lives"} remaining.`);
    },
    onGameOver: async ({ events, depth, scoreEstimate, reverseRoundsCleared, wrongTaps, livesUsed, roundAvgMs }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      announce(`Run complete. Reached depth ${depth}. ${scoreEstimate.toLocaleString()} points.`);
      const revTag = reverseRoundsCleared > 0 ? ` · ${reverseRoundsCleared}↩ reverse rounds` : "";
      const livesTag = livesUsed === 0 ? " · perfect (no lives spent)" : ` · ${livesUsed}♡ lives spent`;
      // v5: terse per-round timing — show the last up-to-3 cleared rounds.
      // The full round_avg_ms array is exposed via roundAvgMs / EchoSummary
      // for any future UI that wants more depth.
      const recent = roundAvgMs.slice(-3).map((ms, j, arr) => {
        const round = depth - (arr.length - 1 - j);
        return `r${round}: ${Math.round(ms)}ms`;
      });
      const timingTag = recent.length > 0 ? ` · ${recent.join(" / ")}` : "";
      msgBody.textContent = `Depth ${depth} · ${scoreEstimate.toLocaleString()} pts (RT bonus on submit)${revTag}${livesTag}${timingTag}`;
      btnStart.textContent = "Try again";
      renderHearts(Math.max(0, 3 - livesUsed));
      showWrongTapHeatmap(wrongTaps);
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
        hudBest.textContent = (Math.max(pb, scoreEstimate)).toLocaleString();
      } catch { /* swallow */ }
      const xp = recordRun({ game: "echo", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("echo", scoreEstimate);

      if (!activeRunId) {
        renderChallengeFooter(scoreEstimate, null);
        return;
      }
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishEchoRun(activeRunId, handle, events, wrongTaps);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
        await handleChallengeOnGameOver(handle, scoreEstimate, activeRunId);
        renderChallengeFooter(scoreEstimate, handle);
      } else {
        pendingEvents = events;
        pendingWrongTaps = wrongTaps;
        submitForm.hidden = false;
      }
    },
  }, seed, { audioOnly, reducedMotion });
  renderHearts(3);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Echo";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingEvents) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishEchoRun(activeRunId, value, pendingEvents, pendingWrongTaps);
  pendingEvents = null;
  pendingWrongTaps = [];
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});

async function handleChallengeOnGameOver(handle: string, score: number, runId: string) {
  if (!challengeCode || !challengeInfo || challengeInfo.expired) return;
  if (challengeInfo.accepter) return;
  const result = await acceptChallenge(challengeCode, handle, score, runId);
  if (!result) return;
  const verdict =
    result.winner === handle ? `🏆 YOU WIN — outscored ${result.challenger.handle} by ${(score - result.challenger.score).toLocaleString()} pts`
    : result.winner === "tie" ? `It's a tie at ${score.toLocaleString()} pts`
    : `${result.challenger.handle} wins — ${(result.challenger.score - score).toLocaleString()} pts ahead`;
  msgBody.textContent += ` · ${verdict}`;
}

function renderChallengeFooter(score: number, handle: string | null) {
  if (challengeCode) return;
  let footer = document.getElementById("chal-footer");
  if (!footer) {
    footer = document.createElement("div");
    footer.id = "chal-footer";
    footer.className = "chal-footer";
    document.querySelector(".screen-msg")?.appendChild(footer);
  }
  if (!handle || !activeSeed) {
    footer.innerHTML = `<small>Set a handle to share a head-to-head challenge with this run's seed.</small>`;
    return;
  }
  footer.innerHTML = `<button type="button" class="btn ghost chal-btn" id="btn-challenge">Challenge a friend →</button><span class="chal-status" id="chal-status"></span>`;
  const btn = document.getElementById("btn-challenge") as HTMLButtonElement;
  const status = document.getElementById("chal-status") as HTMLElement;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Creating…";
    const res = await createChallenge({ game: "echo", seed: activeSeed!, handle, score, runId: activeRunId ?? undefined });
    if (!res) { btn.disabled = false; btn.textContent = "Try again"; status.textContent = "Failed."; return; }
    const link = `${location.origin}/challenge?code=${res.code}`;
    btn.textContent = "Copied!";
    try { await navigator.clipboard?.writeText(link); } catch { /* ignore */ }
    status.innerHTML = `Code <code>${res.code}</code> · <a href="${link}" target="_blank">${link}</a>`;
  });
}
